// src/components/scheduler/CalendarView.tsx
import React from 'react';
import { DayAvailability } from './types';

interface CalendarViewProps {
    availableDates: string[];
    selectedDate: string | null;
    onDateSelect: (date: string) => void;
    availability: Record<string, DayAvailability>;
}

export const CalendarView: React.FC<CalendarViewProps> = ({
    availableDates,
    selectedDate,
    onDateSelect,
    availability
}) => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();

    // Get first day of month and number of days
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    // Generate calendar days
    const calendarDays = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
        calendarDays.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
        calendarDays.push(day);
    }

    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const isDateAvailable = (day: number) => {
        const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        return availableDates.includes(dateStr);
    };

    const isToday = (day: number) => {
        return day === today.getDate() &&
            currentMonth === today.getMonth() &&
            currentYear === today.getFullYear();
    };

    const getDateString = (day: number) => {
        return `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    };

    return (
        <div className="bg-white border border-gray-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-3">
                <button className="text-gray-400 hover:text-gray-600 p-1">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
                <h3 className="text-base font-semibold text-[#212A37]">
                    {monthNames[currentMonth]} {currentYear}
                </h3>
                <button className="text-gray-400 hover:text-gray-600 p-1">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-2">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                    <div key={`${day}-${i}`} className="text-center text-xs font-medium text-gray-500 py-1">
                        {day}
                    </div>
                ))}

            </div>

            <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((day, index) => {
                    if (!day) {
                        return <div key={`empty-${index}`} className="aspect-square" />;
                    }

                    const dateStr = getDateString(day);
                    const available = isDateAvailable(day);
                    const selected = selectedDate === dateStr;
                    const today = isToday(day);

                    return (
                        <button
                            key={day}
                            onClick={() => available && onDateSelect(dateStr)}
                            disabled={!available}
                            className={`
                aspect-square flex items-center justify-center relative rounded-md text-sm font-medium
                transition-all duration-200
                ${available
                                    ? selected
                                        ? 'bg-[#212A37] text-white'
                                        : 'bg-gray-50 hover:bg-gray-100 text-[#212A37] cursor-pointer'
                                    : 'text-gray-300 cursor-not-allowed'
                                }
              `}
                        >
                            {day}
                            {today && (
                                <span className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-[#e2ba57] rounded-full" />
                            )}
                        </button>
                    );
                })}
            </div>

            <div className="mt-4 flex items-center justify-center text-xs text-gray-500">
                <span className="flex items-center">
                    <span className="w-1 h-1 bg-[#e2ba57] rounded-full mr-2"></span>
                    Today
                </span>
            </div>
        </div>
    );
};